/* ═══════════════════════════════════════════════════════════
   Shared behaviour for every page — vanilla JS, no jQuery, no
   slick. Showing and hiding runs on Tailwind's `hidden` class,
   so the state changes need no stylesheet of their own.

   This is both the bundle for pages without any script of their
   own (dist/js/app.js) and the module the page entries import,
   so it ends up inside their bundle too.
   ═══════════════════════════════════════════════════════════ */
import { createModal } from './modal.js';

/* Where the desktop layout starts, read from the stylesheet rather than typed
   again here. `--breakpoint-desk` is the one line that decides it, and a media
   query string cannot hold a custom property — so the value is fetched once at
   start-up. It was written out as `960px` twice, which was right for
   tuinposter.nl and wrong for tuindoek.nl, where the header switches at 1080
   and the mega menus opened on hover from 960 (16-09-2026).

   The fallback is Tailwind's own `lg`, for the moment before the stylesheet has
   landed. */
const DESK =
  getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-desk').trim() || '64rem';

(() => {
  'use strict';

  /* The suspension systems used to be built here: a copy of the ten in
     javascript, with their Dutch descriptions and their image paths, written
     into `[data-suspensions-cards]` and `[data-suspensions-menu]`. No template
     has carried either hook since the home page and the mega menu started
     drawing them from the database, so it built markup nobody asked for and
     shipped it in every bundle. Removed 16-09-2026; the systems come from
     GardenposterFuncs::getSuspensionTypes() and nowhere else. */

  /* ── mobile menu ──
     Below `desk` the menu is a fixed panel from the bottom of the sticky header
     to the bottom of the screen. We measure the `top`, because that height
     varies — an expanded search bar, phone numbers that stack on the
     narrowest screens. The body does not scroll while the menu is open; only
     the panel itself does.

     There used to be a bar of buttons fixed to the bottom of the screen, and
     the panel reserved room for it with a bottom padding. Those buttons are
     at the top of the page now, so the panel runs to the bottom edge. */
  const nav = document.querySelector('[data-menu]');
  const burger = document.querySelector('[data-menu-toggle]');
  const stickyHeader = document.querySelector('[data-sticky-header]');
  const isDesktopNav = () => window.matchMedia(`(min-width: ${DESK})`).matches;

  const positionMenu = () => {
    if (isDesktopNav()) return;
    nav.style.top = `${stickyHeader.offsetHeight}px`;
    nav.style.bottom = '0px';
  };

  const setMenuOpen = (open) => {
    nav.classList.toggle('hidden', !open);
    burger.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('overflow-hidden', open && !isDesktopNav());
    if (open) positionMenu();
  };

  burger?.addEventListener('click', () => {
    setMenuOpen(nav.classList.contains('hidden'));
  });

  window.addEventListener('resize', () => {
    if (isDesktopNav()) {
      document.body.classList.remove('overflow-hidden');
      // The offsets belong to the mobile panel; on desktop the menu bar sits
      // in the flow and would be pushed about by them.
      nav.style.top = nav.style.bottom = '';
    } else if (!nav.classList.contains('hidden')) {
      positionMenu();
    }
  });

  /* ── mobile search bar ── */
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchPanel = document.querySelector('[data-search-panel]');
  const setSearch = (open) => {
    searchPanel.classList.toggle('hidden', !open);
    searchToggle.setAttribute('aria-expanded', String(open));
    if (open) searchPanel.querySelector('input').focus();
  };
  searchToggle?.addEventListener('click', () => {
    setSearch(searchPanel.classList.contains('hidden'));
  });

  /* ── mega-menu ── */
  const setMegaOpen = (btn, open) => {
    btn.nextElementSibling.classList.toggle('hidden', !open);
    btn.setAttribute('aria-expanded', String(open));
  };

  const closeMegas = (except) => {
    document.querySelectorAll('[data-mega]').forEach((b) => {
      if (b === except) return;
      setMegaOpen(b, false);
    });
  };

  /* Panels open on hover only on a desktop with a mouse. Touch has no real
     hover — there (and for keyboard users) clicking stays the way in. */
  const canHover = window.matchMedia(`(hover: hover) and (min-width: ${DESK})`);
  let closeTimer;

  document.querySelectorAll('[data-mega]').forEach((btn) => {
    const panel = btn.nextElementSibling;
    const setOpen = (open) => setMegaOpen(btn, open);

    btn.addEventListener('click', () => {
      const open = panel.classList.contains('hidden');
      closeMegas(btn);
      setOpen(open);
    });

    /* The listeners sit on the <li>, so the panel counts as part of it:
       moving from button to panel is not a mouseleave. The short delay on
       leaving absorbs a pointer that clips the corner on its way past. */
    const item = btn.parentElement;
    item.addEventListener('mouseenter', () => {
      if (!canHover.matches) return;
      clearTimeout(closeTimer);
      closeMegas(btn);
      setOpen(true);
    });
    item.addEventListener('mouseleave', () => {
      if (!canHover.matches) return;
      closeTimer = setTimeout(() => setOpen(false), 150);
    });
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-mega], [data-categories], [data-suspensions]')) closeMegas();
  });

  /* ── the categories buttons: the one under the logo, and the
     "choose from the photo library" block on the home page ──
     The href behind it goes to the library page, which forwards to whichever
     category is first in the list — a page the visitor did not ask for. The
     list of all sixty is already on this page, folded into the main menu, so
     the button opens that instead: the menu, with the categories out.

     A second press closes it again, which is what a button that opens
     something ought to do — and it saves the visitor working out that the
     cross at the top left is the way back. Without javascript nothing here
     runs and the link goes where it says it goes.

     From `desk` up there is no menu to open (the categories sit in the bar,
     on hover), so there the link is left alone and lands on the first
     category — which is what the client wants the home block to do on a
     desktop (2 Oct 2026).

     The suspension systems button beside it does the same with the second
     item of the menu (4 Oct 2026); its href is the overview of the systems. */
  const toggleMega = (mega) => (e) => {
    if (!mega || !nav || isDesktopNav()) return;

    e.preventDefault();

    const showing =
      !nav.classList.contains('hidden') &&
      mega.getAttribute('aria-expanded') === 'true';

    setMenuOpen(!showing);

    /* Folded shut on the way out, so the next visitor to the menu — by this
       button or by the burger — finds it the way everyone else does. */
    if (showing) {
      setMegaOpen(mega, false);

      return;
    }

    closeMegas(mega);
    setMegaOpen(mega, true);

    /* The panel keeps whatever it was scrolled to last time. Scrolled so the
       opened item is the first thing in it: the categories are first anyway,
       the suspension systems sit two items down and would otherwise open
       under the top of the list (client, 5 Oct 2026). Measured after the
       panel is shown, and clamped by the browser where the list runs out. */
    const item = mega.closest('li');
    nav.scrollTop = item.getBoundingClientRect().top - nav.getBoundingClientRect().top + nav.scrollTop;
  };

  [
    ['[data-categories]', '[data-mega-categories]'],
    ['[data-suspensions]', '[data-mega-suspensions]'],
  ].forEach(([buttons, mega]) => {
    const handler = toggleMega(document.querySelector(mega));

    document.querySelectorAll(buttons).forEach((button) => {
      button.addEventListener('click', handler);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeMegas();
    if (searchPanel && !searchPanel.classList.contains('hidden')) {
      setSearch(false);
      searchToggle.focus();
    }
  });

  /* ── gift voucher on the home page: two roads out of one button ──
     Picking an amount and pressing order used to land on the voucher page
     with that amount already filled in, where the only thing left to do was
     press order again. An amount that has been chosen is everything the cart
     needs, so it goes there in one step.

     "Own amount" is the one with an empty value. It opens the field of the
     voucher page inside the form (Steve, 2 Oct 2026, as on tuindoek.nl),
     and what is typed there goes into the cart like a picked amount.

     The form is written as the road to the voucher page, so without
     javascript every choice lands there with the amount filled in, which is
     where this block sent everybody before. Nothing is lost; a step is
     skipped.

     Posted rather than fetched, and to the address the page is already on:
     core answers a post with `action=add` from anywhere with a redirect to
     the cart, so the browser does the navigating and the back button behaves
     the way it should. */
  const giftcard = document.querySelector('[data-giftcard]');

  /* The field stays disabled while it is hidden, so `data-validate` only
     asks for it when it is in view. */
  const ownWrap = giftcard?.querySelector('[data-giftcard-own]');
  const ownField = ownWrap?.querySelector('input');

  /* The amount on the picture of the voucher, in the same words as the
     chips: currency, space, number — or number, space, currency on a shop
     that writes the sign after the amount (posterdejardin.fr). */
  const giftcardValue = document.querySelector('[data-giftcard-value]');

  const pickedAmount = () => {
    const chosen = giftcard.querySelector('input[name="price"]:checked');

    if (!chosen) return { chosen, value: '' };

    return { chosen, value: chosen.value || (ownField && !ownField.disabled ? ownField.value : '') };
  };

  const showAmount = () => {
    if (!giftcardValue) return;

    const { value } = pickedAmount();
    const { before, after } = giftcardValue.dataset;

    giftcardValue.textContent = value ? [before, value, after].filter(Boolean).join(' ') : '';
  };

  const showOwn = (own) => {
    if (!ownWrap) return;

    ownWrap.hidden = !own;
    ownField.disabled = !own;
  };

  giftcard?.addEventListener('change', (event) => {
    if (event.target.name === 'price') {
      const own = event.target.value === '';

      showOwn(own);
      if (own) ownField?.focus();
    }

    showAmount();
  });

  ownField?.addEventListener('input', showAmount);

  /* The browser puts a form back as it was left — after a reload, or back
     from the cart — without telling anyone, so the field and the voucher are
     brought in line with whatever it restored. `pageshow` covers the page
     coming back from the back-forward cache as well. */
  const syncGiftcard = () => {
    const { chosen } = pickedAmount();

    showOwn(chosen?.value === '');
    showAmount();
  };

  if (giftcard) {
    syncGiftcard();
    window.addEventListener('pageshow', syncGiftcard);
  }

  /* Whole euros only (Steve, 26-09-2026 on tuindoek.nl: "geen komma
     getallen"): a number field takes a comma, a point, `e`, `+` and `-` as
     well, so anything but a digit never gets in, not typed, not pasted.
     Keys with a name of their own (Backspace, the arrows, Tab) and
     shortcuts pass. */
  document.querySelectorAll('input[data-whole-number]').forEach((field) => {
    field.addEventListener('keydown', (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) return;
      if (!/\d/.test(event.key)) event.preventDefault();
    });

    field.addEventListener('paste', (event) => {
      const pasted = event.clipboardData?.getData('text') ?? '';
      if (/^\d*$/.test(pasted)) return;

      event.preventDefault();
      field.value = pasted.split(/[,.]/)[0].replace(/\D/g, '');
      field.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });

  giftcard?.addEventListener('submit', (event) => {
    const { chosen, value } = pickedAmount();

    /* A typed amount that will not do is left to `data-validate`, which
       listens after this and stops the form with a red field. */
    if (ownField && !ownField.disabled && !ownField.checkValidity()) return;

    if (!chosen || !value) {
      if (chosen) chosen.disabled = true;

      return;
    }

    event.preventDefault();

    const order = document.createElement('form');

    order.method = 'post';
    order.hidden = true;

    Object.entries({
      action: 'add',
      qty: 1,
      type: giftcard.dataset.cartType,
      // voucher/75.jpg, which voucher.php draws with the amount on it
      image: `${giftcard.dataset.cartImage}/${value}.jpg`,
      price: value,
    }).forEach(([name, value]) => {
      const field = document.createElement('input');

      field.type = 'hidden';
      field.name = name;
      field.value = value;
      order.append(field);
    });

    document.body.append(order);
    order.submit();
  });

  /* ── sticky sidebar ──
     The sidebar is taller than the screen. A fixed `top` would put its bottom
     out of reach, and a scrollbar of its own would give the page two
     scrolling areas. So it follows the reading direction instead: on the way
     down it travels along until its bottom comes into view and stays there,
     and on the way back up it travels along again until its top is under the
     header.

     One line of bookkeeping does that. `top` moves against the scroll, pixel
     for pixel, held between "top under the header" and "bottom in view".
     Between those two ends moving `top` against the scroll is what keeps the
     sidebar still relative to the page, so it comes back up with the rest of
     it; at either end it stops moving and the sidebar sticks.

     The height is measured rather than read on every scroll — writing `top`
     and reading `offsetHeight` in the same breath makes the browser lay the
     page out twice a frame. The observer below says when it changed, which
     for the sidebar is when the list of photographers unfolds. A sidebar that
     grew keeps where it stands and the new rows arrive below it, which is
     calmer than yanking the list up to put its new bottom in view; one that
     shrank is caught by the clamp, which brings its bottom back down to the
     foot of the window instead of leaving it hanging halfway up it.

     More than one per page: the suspension pages have a list of systems on
     one side and the photo of this one on the other, and both follow along. */
  const stickSidebar = (sidebar) => {
    /* Under the sticky header with the 21px (pt-5) the columns start below it:
       measured, because the tagline under the logo wraps onto a second line
       up to `wide` and the header grows with it. */
    const GAP = 21;
    const BOTTOM = 16;

    let TOP = stickyHeader.offsetHeight + GAP;
    let height = sidebar.offsetHeight;
    let previous = Math.max(0, window.scrollY);
    let offset = TOP;

    const place = () => {
      /* Rubber-banding past the top of the page reports a negative scroll
         on ios, and that would carry the sidebar down with it. */
      const y = Math.max(0, window.scrollY);
      /* Where the sidebar stands when its bottom is in view. A sidebar that
         fits on the screen never gets that far: for that one this is below
         the header, and the two ends fall together. */
      const lowest = Math.min(window.innerHeight - height - BOTTOM, TOP);

      offset = Math.min(TOP, Math.max(lowest, offset - (y - previous)));
      previous = y;
      sidebar.style.top = `${offset}px`;
    };

    const remeasure = () => {
      TOP = stickyHeader.offsetHeight + GAP;
      height = sidebar.offsetHeight;
      place();
    };

    remeasure();
    new ResizeObserver(remeasure).observe(sidebar);
    window.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', remeasure, { passive: true });
  };

  document.querySelectorAll('[data-sticky-sidebar]').forEach(stickSidebar);

  /* The sidebar of a list page waits in a <template> below lg (_sidebar.php)
     and is put in place when the screen grows to lg, which can be after this
     has run. */
  document.addEventListener('sidebar:filled', (event) => {
    event.target.querySelectorAll('[data-sticky-sidebar]').forEach(stickSidebar);
  });

  /* ── sidebar: the whole list of photographers ──
     The box names six and carries the rest in the same <ul>, hidden. It used
     to be a link to the artists page, but that url shows nothing of its own —
     it redirects to whichever photographer sorts first — so the list belongs
     here.

     Two buttons work the one list: "alle 43 fotografen" under it opens, "toon
     minder" in the title bar closes. Neither is ever on screen at the same
     time as the other — the stylesheet shows and hides them on
     `aria-expanded` — so the attribute goes on both of them here, and the
     button that was clicked is not treated any differently from the one that
     was not.

     The sticky sidebar above watches its own height, so the `top` is worked
     out again the moment the list grows.

     One listener on the document: the sidebar may arrive after start-up
     (see `sidebar:filled` above). */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-artists-toggle]');

    if (!toggle) return;

    const open = toggle.getAttribute('aria-expanded') !== 'true';

    document.querySelectorAll('[data-artists-toggle]').forEach((each) => each.setAttribute('aria-expanded', String(open)));
    document.querySelectorAll('[data-artists-rest]').forEach((row) => row.classList.toggle('hidden', !open));
  });

  /* ── faq: the questions fold open ──
     The markup is built by FaqFuncs::getHTML() in core, so there is nothing
     to hang a data-attribute on and no button to be found: the clickable part
     is a plain <div>. That is also why the roles are handed out here rather
     than in a template — a div that reacts to a click has to be reachable
     with a keyboard, and has to say whether it is open.

     Note this sits above the slider, which bows out with an early return on
     any page that has no hero. */
  const questions = document.querySelectorAll('.faq-question');

  questions.forEach((question) => {
    const head = question.querySelector('.faq-question--question');
    const body = question.querySelector('.faq-question--answer');

    if (!head || !body) return;

    head.setAttribute('role', 'button');
    head.setAttribute('tabindex', '0');
    head.setAttribute('aria-expanded', 'false');

    const toggle = () => {
      const open = question.classList.toggle('is-open');
      head.setAttribute('aria-expanded', String(open));
    };

    head.addEventListener('click', toggle);

    /* Enter and space are what a button answers to, and space scrolls the
       page unless it is stopped here. */
    head.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      toggle();
    });
  });

  /* ── endless scrolling ──
     For any listing that marks its container `data-endless` and puts a
     `data-paging` block under it: the photo grids and the customer reactions.

     Enhancement on top of what the server sent. The page links are real
     <a href>s and the list is rendered server-side, so without this the
     visitor pages through by hand and a crawler follows rel=next
     (PLAN.md section 2.6).

     Note this sits above the slider, which bows out with an early return on
     any page that has no hero. */
  const endless = document.querySelector('[data-endless]');
  const paging = document.querySelector('[data-paging]');

  if (endless && paging) {
    /* The next page is fetched as a page, not as a fragment: no endpoint of
       its own to keep in step with the template, and what comes back is by
       definition the same markup as page one. */
    let next = paging.querySelector('a[rel="next"]')?.href ?? null;
    let busy = false;

    /* Out of sight now that we are here to do the paging, but still in the
       document: it is the crawlable foundation under this and the way back if
       a fetch fails. Hiding it is javascript's job for the same reason — with
       the css doing it, someone without javascript would be left on page one
       with no way forward. */
    const sentinel = document.createElement('div');

    sentinel.setAttribute('aria-hidden', 'true');
    paging.before(sentinel);
    paging.hidden = true;

    const more = async () => {
      if (!next || busy) return;

      busy = true;
      paging.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(next, { credentials: 'same-origin' });

        if (!response.ok) throw new Error(response.status);

        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const batch = page.querySelector('[data-endless]');

        if (batch) endless.append(...batch.children);

        /* Take the paging of the page we just loaded, so "next" moves along
           and the numbers keep saying where we are. */
        const fetched = page.querySelector('[data-paging]');

        next = fetched?.querySelector('a[rel="next"]')?.href ?? null;

        /* Keep it in step even while it is hidden: the moment a fetch fails
           it comes back into view, and then it has to point at the page after
           the last one we managed to load. */
        if (fetched) paging.innerHTML = fetched.innerHTML;
        if (!next) sentinel.remove();

        /* For whoever holds a model of what is on the page — the lightbox in
           category.js walks the tiles, and there are more of them now. */
        endless.dispatchEvent(new CustomEvent('endless:batch'));
      } catch {
        /* Hand the paging back to the visitor: the links still work, so a
           failed fetch falls back to what the page could already do. */
        next = null;
        sentinel.remove();
        paging.hidden = false;
      } finally {
        busy = false;
        paging.removeAttribute('aria-busy');
      }
    };

    /* An empty marker where the paging used to be. It has to be something
       with a box of its own — a hidden element has no geometry, so observing
       the paging itself would never fire. The margin starts the fetch while
       it is still a screen away. */
    new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) more();
      },
      { rootMargin: '800px 0px' }
    ).observe(sentinel);
  }

  /* ── lightbox ──
     Every `data-lightbox` link on the page is a photo to be enlarged, and
     together they are one set to walk through — the arrangement magnific-popup
     had with `gallery: true`. The markup of the dialog is _lightbox.php, so
     the labels stay in the webcopy.

     Enhancement, like everything else here: the links point at the image file
     itself, so without this they simply open it.

     The set is read on every open rather than captured once. The example
     gardens grow while you scroll, and a photo that arrived with the third
     batch has to walk with the rest. */
  const lightbox = document.querySelector('[data-lightbox-modal]');

  if (lightbox) {
    const frame = lightbox.querySelector('[data-lightbox-img]');
    const counter = lightbox.querySelector('[data-lightbox-counter]');
    const previous = lightbox.querySelector('[data-lightbox-prev]');
    const following = lightbox.querySelector('[data-lightbox-next]');
    const closer = lightbox.querySelector('[data-lightbox-close]');
    const area = lightbox.querySelector('[data-lightbox-area]');

    let photos = [];
    let current = 0;
    let openedFrom = null;

    const show = (index) => {
      current = (index + photos.length) % photos.length;

      const photo = photos[current];

      frame.src = photo.href;
      /* The alt of the thumbnail describes the same picture, so it is the one
         to carry over; an empty one stays empty and the photo reads as
         decoration, which beats inventing a description. */
      frame.alt = photo.querySelector('img')?.alt ?? '';

      const many = photos.length > 1;

      counter.textContent = many ? `${current + 1} / ${photos.length}` : '';
      previous.hidden = following.hidden = !many;
    };

    const open = (index, from) => {
      openedFrom = from;
      show(index);
      lightbox.classList.remove('hidden');
      document.documentElement.classList.add('overflow-hidden');
      closer.focus();
    };

    const close = () => {
      lightbox.classList.add('hidden');
      document.documentElement.classList.remove('overflow-hidden');
      /* Let go of the photo, or a big one stays in memory for the rest of the
         visit and the next open shows it for a frame before the new one is
         decoded. */
      frame.removeAttribute('src');
      openedFrom?.focus();
    };

    /* One handler on the document: the links are spread over the page and on
       the example gardens they keep arriving. */
    document.addEventListener('click', (event) => {
      const photo = event.target.closest('a[data-lightbox]');

      if (!photo) return;

      /* Anything but a plain left click is someone who wants the file itself:
         a new tab, a bookmark, a middle click. Let the link be a link. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();
      photos = [...document.querySelectorAll('a[data-lightbox]')];
      open(photos.indexOf(photo), photo);
    });

    closer.addEventListener('click', close);
    previous.addEventListener('click', () => show(current - 1));
    following.addEventListener('click', () => show(current + 1));

    /* Clicking beside the photo closes it; on the photo itself it does not.
       The old theme closed on the photo as well (closeOnContentClick), but
       that was a dialog you could only leave — with arrows beside it a
       mis-aimed click on the picture should not throw you out, and on a phone
       the end of a swipe lands there. */
    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox || event.target.dataset.lightboxArea !== undefined) close();
    });

    document.addEventListener('keydown', (event) => {
      if (lightbox.classList.contains('hidden')) return;

      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') show(current - 1);
      if (event.key === 'ArrowRight') show(current + 1);
    });

    /* Swiping. 40 px is enough to tell a swipe from a tap, and the movement
       has to be mostly horizontal — otherwise someone is scrolling. */
    let startX = 0;
    let startY = 0;

    area.addEventListener(
      'touchstart',
      (event) => {
        startX = event.changedTouches[0].clientX;
        startY = event.changedTouches[0].clientY;
      },
      { passive: true }
    );

    area.addEventListener(
      'touchend',
      (event) => {
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;

        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(current + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );
  }

  /* ── video ──
     A `data-video` link plays its film in _video.php instead of leaving for
     YouTube or Vimeo. The watch address on the link is all it needs: the id comes out
     of it, and a link no id can be read from stays an ordinary link.

     youtube-nocookie is the same player without the cookies until the film
     plays — and it is only loaded once somebody has asked for it. */
  const videoOverlay = document.querySelector('[data-dialog-video]');

  if (videoOverlay) {
    const videoFrame = videoOverlay.querySelector('[data-video-frame]');
    const videoPanel = videoOverlay.querySelector('[data-modal-panel]');
    const videoDialog = createModal(videoOverlay, { onRequestClose: () => closeVideo() });

    /* Taking the iframe out is what stops the sound; hiding it would not. */
    const closeVideo = () => {
      videoFrame.replaceChildren();
      videoDialog.close();
    };

    /* The player address for a watch address, '' for one it cannot read.
       YouTube for every shop; Vimeo for the film outdoorcanvas.co.uk keeps
       from the old theme. dnt=1 is Vimeo's own no-cookies switch. */
    const videoEmbed = (href) => {
      try {
        const url = new URL(href);

        if (url.hostname.endsWith('vimeo.com')) {
          const id = url.pathname.split('/').filter(Boolean).pop();

          return /^\d+$/.test(id ?? '') ? `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1` : '';
        }

        const id = url.hostname.endsWith('youtu.be') ? url.pathname.slice(1) : url.searchParams.get('v');

        return /^[\w-]{11}$/.test(id ?? '') ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0` : '';
      } catch {
        return '';
      }
    };

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-video]');

      if (!link) return;

      /* A new tab, a bookmark, a middle click: let the link be a link. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      const src = videoEmbed(link.href);

      if (!src) return;

      event.preventDefault();

      const player = document.createElement('iframe');

      player.src = src;
      player.title = link.title;
      player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      player.allowFullscreen = true;
      player.className = 'size-full border-0';

      videoFrame.replaceChildren(player);
      videoPanel.setAttribute('aria-label', link.title);
      videoDialog.open();
    });

    videoOverlay.querySelector('[data-close-video]').addEventListener('click', closeVideo);
  }

  /* ── contact form: the check runs on send ──
     The widget is rendered with `execution="execute"`, so it sits there doing
     nothing until asked. Asking happens here, on the submit of the form —
     which means the browser has already been over the required fields, since
     that event does not fire until they pass. Nobody is measured for filling
     in a form they never send.

     The condition is the token itself and not a flag of our own: turnstile
     writes it into the form as `cf-turnstile-response`, so "do we have one"
     is a question the form can answer. Its callback puts it there and asks
     the form to submit again, and the second time round this lets it through.

     Without javascript none of this runs — but neither does turnstile, which
     is javascript from end to end, so there was never a way through without
     it. The server turns down a post without a token either way. */
  const contactForm = document.querySelector('[data-contact-form]');
  const widget = document.querySelector('[data-turnstile]');

  if (contactForm && widget) {
    const token = () => contactForm.querySelector('[name="cf-turnstile-response"]')?.value;

    let asking = false;

    contactForm.addEventListener('submit', (event) => {
      if (token()) return;

      event.preventDefault();

      /* A second click while the challenge is up would start another one. */
      if (asking) return;

      asking = true;
      window.turnstile?.execute(widget);
    });

    window.onTurnstileContact = () => {
      asking = false;
      contactForm.requestSubmit();
    };

    /* Five minutes is the life of a token. Reaching that between the check
       and the send is close to impossible now that the two are one action,
       but if it happens the next click asks again rather than sending
       something the server is going to refuse. */
    window.onTurnstileContactExpired = () => {
      asking = false;
      window.turnstile?.reset(widget);
    };
  }

  /* What the thing that asked was going to do, once the answer is yes. A link
     goes to its address; a button inside a form submits it. */
  let pending = null;

  const proceed = (element) => {
    if (element.tagName === 'A') {
      window.location = element.href;

      return;
    }

    element.form?.requestSubmit(element.type === 'submit' ? element : undefined);
  };

  /* ── checkout: the address fields ──
     Three habits the old theme had and lost with jQuery. The fourth — showing
     the second address when it is not the same as the first — is css now
     (`group-has-checked`), so it works without any of this.

     A country picked out of the radios — or out of the dropdown that takes
     their place from four countries on — decides two of them, so the work is
     done per address block and re-done whenever the country changes. */
  document.querySelectorAll('[class*="group/billing"], [class*="group/shipping"]').forEach((block) => {
    const type = block.className.includes('group/billing') ? 'billing' : 'shipping';
    const at = (field) => block.querySelector(`[name="${type}_${field}"]`);

    const box = at('box');
    const houseNumber = at('house_number');
    const postalCode = at('postal_code');
    const vat = at('vat');
    const warning = block.querySelector('[data-box-warning]');

    /* Countries that do not put a vat number on an invoice: the field is
       hidden and emptied, so nothing travels along that the server would then
       have to judge. The class is what css hangs the hiding off. */
    const WITHOUT_VAT = ['NL'];

    const country = () =>
      block.querySelector(`[name="${type}_country"]:checked, input[type="hidden"][name="${type}_country"]`)?.value ?? '';

    const placeVat = () => {
      const without = WITHOUT_VAT.includes(country());

      block.classList.toggle('no-vat', without);
      if (without && vat) vat.value = '';
    };

    /* An addition that is the postal code over again is a slip of the hand and
       is simply dropped. One that is the house number over again might be
       right — a number with a letter behind it — so that one is asked about
       rather than decided. */
    const checkBox = () => {
      if (!box || !warning) return;

      const value = box.value.trim().toLowerCase();

      warning.hidden = true;

      if (!value) return;

      if (value === postalCode?.value.trim().toLowerCase()) {
        box.value = '';

        return;
      }

      if (value === houseNumber?.value.trim().toLowerCase()) warning.hidden = false;
    };

    [box, houseNumber, postalCode].forEach((field) => {
      if (!field) return;

      /* A space in a postal code or a house number is never meant — except
         where the shop takes any text there (data-keep-spaces): a British
         address has house names and flats. */
      if (!field.hasAttribute('data-keep-spaces')) {
        field.addEventListener('input', () => {
          field.value = field.value.replace(/\s/g, '');
        });
      }

      field.addEventListener('blur', checkBox);
    });

    warning?.querySelector('[data-box-keep]')?.addEventListener('click', () => {
      warning.hidden = true;
    });

    warning?.querySelector('[data-box-clear]')?.addEventListener('click', () => {
      box.value = '';
      warning.hidden = true;
      box.focus();
    });

    /* A radio and the dropdown's hidden input both fire a bubbling change. */
    block.addEventListener('change', (event) => {
      if (event.target.name === `${type}_country`) placeVat();
    });

    placeVat();
  });

  /* ── cart: the quantity applies itself ──
     A control with a button beside it to make it take effect is a control
     nobody trusts, so changing the number sends its own little form. That was
     an `onchange` attribute in the old markup; the <noscript> beside it is the
     button for whoever does not run this.

     Zero is not a quantity, it is a deletion — that is how the server has
     always read it, and the select this replaced offered it as its first
     option, one flick away, with nothing asked. So a number that lands on or
     below zero hands over to the bin of the same row instead, and the bin
     already carries `data-confirm`: the question below picks it up and the
     row only goes once it has been answered. */
  const cartRemove = (field) => {
    const bin = field.closest('li')?.querySelector('[data-confirm]');

    if (bin) {
      bin.click();
      return true;
    }

    return false;
  };

  document.addEventListener('change', (event) => {
    const field = event.target.closest('[data-cart-qty]');

    if (!field) return;

    /* Typed rather than stepped, so it can be anything — including empty,
       which valueAsNumber reports as NaN. */
    const value = Math.floor(field.valueAsNumber);
    const max = Number(field.max);

    if (!Number.isFinite(value) || value < 0) {
      field.value = field.defaultValue;
      return;
    }

    if (value === 0) {
      /* Put the old number back first: the question can still be answered
         with "no", and a row left standing should not read as zero. */
      field.value = field.defaultValue;
      if (cartRemove(field)) return;
    }

    field.value = String(Math.min(value, max));
    field.form?.requestSubmit();
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-qty-step]');

    if (!button) return;

    const field = button.form.querySelector('[data-cart-qty]');
    const next = Math.floor(field.valueAsNumber || 0) + Number(button.dataset.qtyStep);

    if (next < 1) {
      cartRemove(field);
      return;
    }

    field.value = String(Math.min(next, Number(field.max)));
    field.form.requestSubmit();
  });

  /* ── anything that asks first ──
     `data-confirm` carries the question and _confirm.php the box it is asked
     in. The old theme had this and it did not come along, so the bin in the
     cart deleted a row on the first click with nothing in between.

     Without the box on the page it falls back to the grey bar of the browser,
     so a page that forgets to include the partial still asks. */
  const asker = document.querySelector('[data-confirm-modal]');

  document.addEventListener('click', (event) => {
    const asks = event.target.closest('[data-confirm]');

    if (!asks) return;

    event.preventDefault();

    if (!asker) {
      if (window.confirm(asks.dataset.confirm)) proceed(asks);

      return;
    }

    pending = asks;
    asker.querySelector('[data-confirm-text]').textContent = asks.dataset.confirm;
    asker.classList.remove('hidden');
    document.documentElement.classList.add('overflow-hidden');

    /* No has the focus: a return pressed out of habit should not be the
       answer that cannot be taken back. */
    asker.querySelector('[data-confirm-cancel]').focus();
  });

  if (asker) {
    const shut = () => {
      asker.classList.add('hidden');
      document.documentElement.classList.remove('overflow-hidden');
      pending?.focus();
      pending = null;
    };

    asker.querySelector('[data-confirm-cancel]').addEventListener('click', shut);

    asker.querySelector('[data-confirm-ok]').addEventListener('click', () => {
      const going = pending;

      shut();
      if (going) proceed(going);
    });

    /* Beside the box closes it, the same as a no. */
    asker.addEventListener('click', (event) => {
      if (event.target === asker) shut();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !asker.classList.contains('hidden')) shut();
    });
  }

  /* ── gift voucher: the amount lands on the voucher ──
     The picture beside the field carries "ter waarde van €" and the number
     goes after it, so what is typed shows up on the voucher while it is being
     typed. Server-rendered to begin with — the home page can send an amount
     along in the address — and this only keeps it in step from there.

     `input` and not `keyup`: it fires for the arrows of a number field, for
     pasting and for the spinner as well, which keyup does not. */
  const amount = document.querySelector('[data-voucher-amount]');
  const preview = document.querySelector('[data-voucher-preview]');

  if (amount && preview) {
    amount.addEventListener('input', () => {
      preview.textContent = amount.value;

      // the picture the cart gets, with this amount on it — see voucher.php
      if (amount.form?.elements.image) amount.form.elements.image.value = `voucher/${amount.value}.jpg`;
    });
  }

  /* ── toast: a short word at the bottom of the screen ──
     The region is in _footer.php. A new word replaces the one before it and
     starts the clock again; five seconds, as the legacy theme had it. */
  const toastBox = document.querySelector('[data-toast]');
  let toastTimer = 0;

  const toast = (message, icon) => {
    if (!toastBox || !message) return;

    toastBox.querySelector('[data-toast-text]').textContent = message;
    toastBox.querySelector('[data-toast-icon]')?.setAttribute('href', `#${icon}`);
    toastBox.dataset.shown = '';

    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => delete toastBox.dataset.shown, 5000);
  };

  /* ── favourites ──
     The heart under a photo, and the one in the lightbox. Neither of them did
     anything at all: the legacy app.js had this and it did not come along, so
     the button changed nothing, the counter never moved and the wishlist page
     could only be filled by hand through ?add= in the address.

     One handler on the document, because the hearts arrive with every batch
     of endless scrolling and there is one in a dialog that is refilled on
     every open.

     The server is the only one who knows: it answers with the new state and
     the new count, and that is what is written back. No guessing beforehand —
     a heart that flips and then flips back is worse than one that waits. */
  const FAVOURITE = '[data-favorite], [data-modal-favorite]';

  document.addEventListener('click', async (event) => {
    const button = event.target.closest(FAVOURITE);

    if (!button) return;

    /* The tile is a link to the detail page and the heart sits inside it. */
    event.preventDefault();

    /* The dialog carries the id of the photo it is showing in `data-for`. */
    const id = button.dataset.favorite || button.dataset.for || '';

    if (!id || button.disabled) return;

    button.disabled = true;

    try {
      const response = await fetch(
        `/site/services/?action=toggleWishlist&id=${encodeURIComponent(id)}`,
        { credentials: 'same-origin' }
      );

      if (!response.ok) throw new Error(response.status);

      const result = await response.json();
      const added = result.action === 'added';

      /* Every button for this photo: the one on the tile and the one in the
         dialog, which have to say the same thing when the dialog closes. */
      document
        .querySelectorAll(`[data-favorite="${CSS.escape(id)}"], [data-modal-favorite][data-for="${CSS.escape(id)}"]`)
        .forEach((one) => {
          one.setAttribute('aria-pressed', String(added));

          /* The label on the button says which state it is in — css swaps
             the two that are on the page. The title says what a click will
             do next, which is the other one of the pair. */
          one.title = added ? T.removeFromWishlist : T.addToWishlist;
        });

      /* The badge in the header and the one in the sidebar. Hidden at zero,
         the way the templates render it — with the `hidden` attribute and no
         longer with an inline `display`, which was the last style attribute
         this theme wrote by hand. `.count-badge` is a grid, so `hidden` needs
         the display rule in app.css to hold. */
      if (typeof result.num_items !== 'undefined') {
        document.querySelectorAll('[data-favorites="num"]').forEach((badge) => {
          badge.textContent = result.num_items;
          badge.hidden = !result.num_items;
        });
      }

      /* The heart changes where the visitor is looking, but the counter that
         moved sits in the header — out of sight once they have scrolled. So
         say what happened, the way the legacy theme did. */
      toast(added ? T.wishlistAdded : T.wishlistRemoved, added ? 'i-heart-solid' : 'i-heart');

      /* On the wishlist itself a photo that is taken out has to leave: the
         list is what it is a list of, and a tile that stays behind says it is
         still in there. */
      if (!added && document.querySelector('[data-wishlist]')) {
        button.closest('li')?.remove();
      }
    } catch {
      /* Nothing changed, so nothing is written back. The page still works;
         the next click can try again. */
    } finally {
      button.disabled = false;
    }
  });

  /* ── hero sliders: scroll-snap + buttons + dots ──
     Two of them on the home page — the desktop slider and the mobile slider
     are two lists of their own length, so they are two tracks and css shows
     the one that belongs to the screen (page_home.php). Hence a function over
     every [data-slider] rather than one track picked out of the document.

     Swiping works natively; JS only adds controls and auto-advance. It stops
     for good as soon as the visitor takes over. */
  const startSlider = (root) => {
    /* The slider the screen does not show still has its slides after the
       first in a <template> (page_home.php). Counted now it would be a slider
       of one, which loses its controls below for good — so it waits until the
       screen crosses `md` and the script under it puts the rest in place. */
    if (root.querySelector('template[data-slides-rest]')) {
      root.addEventListener('slider:filled', () => startSlider(root), { once: true });
      return;
    }

    const track = root.querySelector('[data-track]');
    const slides = track ? [...track.children] : [];

    /* One banner is not a carousel: nothing to click and nowhere to advance
       to. Two shops out of nineteen are in exactly that position. */
    if (slides.length < 2) {
      root.querySelectorAll('[data-prev], [data-next], [data-counter]').forEach((b) => b.remove());
      return;
    }

    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Dots, a counter, or neither — page_home.php decides, on how many slides
       there are and on which track this is. Whichever it turns out to be, this
       only ever writes the position into it. */
    const dots = root.querySelector('[data-dots]');
    const counter = root.querySelector('[data-counter]');
    let index = 0;
    let timer = null;

    const paint = () => {
      if (counter) {
        counter.textContent = `${index + 1} / ${slides.length}`;
        return;
      }

      /* Neither, which is the phone track: it carries no pager at all since
         27 Aug 2026 (page_home.php), and everything else here — the height
         that follows the slide, the auto-advance — still has to run. */
      if (!dots) return;

      [...dots.children].forEach((d, n) => {
        const on = n === index;
        d.setAttribute('aria-selected', String(on));
        d.className = on
          ? 'h-2.5 w-7 rounded-full bg-white cursor-pointer transition-all'
          : 'h-2.5 w-2.5 rounded-full bg-white/45 hover:bg-white/70 cursor-pointer transition-all';
      });
    };

    /* The other slider is display:none and has no width, so scrolling it is
       scrolling to nought and reading its position is a division by zero.
       Nothing happens to a track that is not on screen; it starts where it
       stands the moment css hands it the screen. */
    const showing = () => track.clientWidth > 0;

    /* ── the box is as tall as the slide in it ──
       No shape is forced on a banner any more (page_home.php), so a list can
       hold slides of two heights, and a flex row is as tall as the tallest of
       them: every shorter slide then sat in a band of bg-ink. The client is the
       one who decides what a slide looks like, so the page takes the height the
       photograph actually has and the box follows it.

       Not the height of `index` but of where the scrolling is: a swipe is
       halfway between two slides for as long as it lasts, and a box that
       snapped from one height to the other at the halfway mark is a jump in the
       middle of a gesture. Between the two neighbours, in proportion, it is one
       continuous movement — and at rest it is exactly the slide on screen.

       The heights are measured by the observer below and not here, because this
       runs on every scroll event and reading a height forces the layout the
       scrolling has just invalidated.

       The height goes on the root, which clips, and never on the track. A
       scroll container that changes size in the middle of a swipe makes the
       browser re-snap it while the finger is still moving, and on a phone the
       swipe then came to rest halfway between two photos (client, 4 Oct 2026).
       The track keeps the height of its tallest slide and so the same snap
       area from start to finish; only the window onto it grows and shrinks. */
    const heights = slides.map(() => 0);

    const fit = () => {
      if (!showing()) return;

      const at = track.scrollLeft / track.clientWidth;
      const left = Math.min(Math.max(Math.floor(at), 0), slides.length - 1);
      const right = Math.min(left + 1, slides.length - 1);
      const part = Math.min(Math.max(at - left, 0), 1);

      const height = heights[left] * (1 - part) + heights[right] * part;

      if (height > 0) root.style.height = `${Math.round(height)}px`;
    };

    /* What makes a slide change height: the photo landing, the window being
       dragged, a phone turning, css handing this track the screen. All of them
       are a resize of the figure, so one observer covers the lot — and it fires
       once per slide on its own the moment it starts, which is the first
       measurement. */
    const watcher = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const i = slides.indexOf(entry.target);

        if (i >= 0) heights[i] = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      }

      fit();
    });

    slides.forEach((slide) => watcher.observe(slide));

    const goTo = (i) => {
      if (!showing()) return;

      index = (i + slides.length) % slides.length;
      track.scrollTo({ left: track.clientWidth * index, behavior: reduce ? 'auto' : 'smooth' });
      paint();
    };

    if (dots) {
      slides.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', `Slide ${i + 1}`);
        b.addEventListener('click', () => {
          stop();
          goTo(i);
        });
        dots.append(b);
      });
    }

    const sync = () => {
      if (!showing()) return;

      fit();

      const i = Math.round(track.scrollLeft / track.clientWidth);
      if (i === index) return;
      index = i;
      paint();
    };

    const stop = () => {
      clearInterval(timer);
      timer = null;
    };
    const start = () => {
      if (reduce || timer) return;
      // three seconds a slide; seven was too long (Steve, 07-10-2026)
      timer = setInterval(() => goTo(index + 1), 3000);
    };

    track.addEventListener('scroll', sync, { passive: true });
    track.addEventListener('pointerdown', stop, { passive: true });
    root.querySelectorAll('[data-prev]').forEach((b) =>
      b.addEventListener('click', () => {
        stop();
        goTo(index - 1);
      })
    );
    root.querySelectorAll('[data-next]').forEach((b) =>
      b.addEventListener('click', () => {
        stop();
        goTo(index + 1);
      })
    );
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    paint();
    start();
  };

  /* ── the caption pane is as wide as its longest line ──
     From `md` the pane is `w-fit` (page_home.php), and that is not enough: the
     heading wraps with `text-wrap: balance`, and a box sizes itself to the text
     on one line before balance breaks it in two. So a heading of two balanced
     lines sat in a pane as wide as the 42rem ceiling, with a band of empty glass
     beside it (client, 4 Oct 2026).

     What is measured is the text itself — the line boxes of every text node —
     and the buttons, which are boxes of their own. Block elements are not: a
     <p> is as wide as the pane whatever it holds. Below `md` the pane is a band
     across the photo and is left alone; a caption in the hidden slider has no
     boxes and is skipped until css hands it the screen. */
  /* Looked up each time rather than once: the captions of a slider that is
     filled later (see startSlider) are not in the page yet at start-up. */
  const captions = () => [...document.querySelectorAll('.slide-caption')];
  const wide = matchMedia('(min-width: 48rem)');

  const fitCaption = (caption) => {
    caption.style.width = '';

    if (!wide.matches || !caption.offsetWidth) return;

    const left = caption.getBoundingClientRect().left;
    const range = document.createRange();
    const walker = document.createTreeWalker(caption, NodeFilter.SHOW_TEXT);
    let right = 0;

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (!node.textContent.trim()) continue;

      range.selectNodeContents(node);

      for (const rect of range.getClientRects()) right = Math.max(right, rect.right);
    }

    caption.querySelectorAll('a').forEach((link) => {
      right = Math.max(right, link.getBoundingClientRect().right);
    });

    if (!right) return;

    const padding = parseFloat(getComputedStyle(caption).paddingRight) || 0;

    caption.style.width = `${Math.ceil(right - left + padding)}px`;
  };

  const fitCaptions = () => captions().forEach(fitCaption);

  if (document.querySelector('[data-slider]')) {
    fitCaptions();
    document.fonts?.ready.then(fitCaptions);
    addEventListener('resize', fitCaptions, { passive: true });
    document.addEventListener('slider:filled', fitCaptions);
  }

  document.querySelectorAll('[data-slider]').forEach(startSlider);
})();

/* ═══════════════════════════════════════════════════════════
   "Hoe hebt u ons gevonden?" on the thank-you page.

   Its own IIFE: the one above ends with the hero slider, which
   returns early on every page that has no slider, so anything
   appended inside it would never run.

   This is the jQuery that left with bootstrap, written again.
   Without it the select opened nothing, the form posted to a
   page that reads no $_POST — and that post reloaded a page
   whose guard sends an empty cart back to the basket, which
   after finalizeOrder() it always is. So answering the question
   threw the customer out of the confirmation.

   The endpoint is the one that was there all along and was
   never reached: /site/services/?action=saveHowDidYouFindUs.
   ═══════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  const form = document.querySelector('[data-find-us]');
  if (!form) return;

  /* The hidden input of the dropdown above, not a <select> any more. It
     carries the same name, so this reads the same and the endpoint receives
     the same field; and the dropdown fires a bubbling `change` on it, so the
     listener below did not have to change either. */
  const source = form.elements.source;
  const toggle = form.querySelector('[data-dropdown-toggle]');
  const extra = form.querySelector('[data-find-us-extra]');
  const labels = [...form.querySelectorAll('[data-find-us-label]')];
  const thanks = document.querySelector('[data-find-us-thanks]');
  const button = form.querySelector('button[type="submit"]');

  /* Only two of the four answers have a follow-up question. The field opens
     for those and carries the label belonging to the answer; for the other
     two it closes again, and its value goes with it — otherwise a keyword
     typed under "google" would travel along with "via vrienden". */
  const sync = () => {
    const label = labels.find((l) => l.dataset.findUsLabel === source.value);

    labels.forEach((l) => (l.hidden = l !== label));
    extra.hidden = !label;

    if (!label) form.elements.extra.value = '';
  };

  source.addEventListener('change', sync);
  sync();

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    /* The <select> carried `required` and the browser did this for us. A
       hidden input cannot, so the check moves here: without an answer there
       is nothing worth posting. */
    if (!source.value) {
      toggle.setAttribute('aria-invalid', 'true');
      toggle.focus();
      return;
    }

    button.disabled = true;

    try {
      const response = await fetch('/site/services/?action=saveHowDidYouFindUs', {
        method: 'POST',
        credentials: 'same-origin',
        body: new URLSearchParams({
          orderId: form.elements.order_id.value,
          source: source.value,
          extra: form.elements.extra.value,
        }),
      });

      if (!response.ok) throw new Error(response.status);

      form.hidden = true;
      thanks.hidden = false;
    } catch {
      /* Leave the form standing and let them try again. There is nothing
         useful to say here: the order is placed either way, and this is a
         question we asked them. */
      button.disabled = false;
    }
  });
})();

/* ═══════════════════════════════════════════════════════════
   Dropdown — our own, in place of a native <select>.

   Markup it drives, all of it required:

     <div data-dropdown data-dropdown-placeholder="Maak uw keuze">
       <input type="hidden" name="…" data-dropdown-input>
       <button type="button" data-dropdown-toggle aria-expanded="false">
         <span data-dropdown-label>Maak uw keuze</span>
         <svg>…chevron…</svg>
       </button>
       <ul data-dropdown-list role="listbox" hidden>
         <li role="option" data-value="…">…</li>
       </ul>
     </div>

   The value lives in the hidden input, so a form posts the same
   field name it always did. Choosing fires a `change` on that
   input, bubbling, so anything watching the form hears it the
   way it would hear a real select.

   Keyboard is the point of writing this rather than styling a
   box: a listbox nobody can reach with a keyboard is worse than
   the browser's own. Arrows move, Enter and Space choose, Escape
   closes, Tab closes and moves on. `data-active` marks where the
   arrows are; aria-selected marks what is chosen — on a listbox
   those are two different things.
   ═══════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  const dropdowns = [...document.querySelectorAll('[data-dropdown]')];
  if (!dropdowns.length) return;

  dropdowns.forEach((root) => {
    const input = root.querySelector('[data-dropdown-input]');
    const toggle = root.querySelector('[data-dropdown-toggle]');
    const label = root.querySelector('[data-dropdown-label]');
    const list = root.querySelector('[data-dropdown-list]');
    const chevron = toggle.querySelector('svg');
    const options = [...list.querySelectorAll('[role="option"]')];
    const placeholder = root.dataset.dropdownPlaceholder || label.textContent.trim();

    const isOpen = () => !list.hidden;

    const setActive = (option) => {
      options.forEach((o) => delete o.dataset.active);

      if (!option) return;

      option.dataset.active = '';
      /* `nearest`, so opening on the chosen option does not jump a short list
         around; only a value below the fold scrolls into view. */
      option.scrollIntoView({ block: 'nearest' });
    };

    const open = () => {
      list.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      chevron.classList.add('rotate-180');
      setActive(options.find((o) => o.dataset.value === input.value) || options[0]);
    };

    const close = () => {
      list.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      chevron.classList.remove('rotate-180');
      setActive(null);
    };

    const choose = (option) => {
      input.value = option ? option.dataset.value : '';
      label.textContent = option ? option.textContent.trim() : placeholder;
      label.classList.toggle('text-ink-faint', !option);
      label.classList.toggle('text-ink', !!option);
      options.forEach((o) => o.setAttribute('aria-selected', String(o === option)));
      toggle.removeAttribute('aria-invalid');
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const move = (step) => {
      const active = options.find((o) => 'active' in o.dataset);
      const from = active ? options.indexOf(active) : -1;
      setActive(options[Math.min(options.length - 1, Math.max(0, from + step))]);
    };

    toggle.addEventListener('click', () => (isOpen() ? close() : open()));

    /* mousedown and not click: the list closes on focusout, and by the time a
       click landed the option was already gone from under the pointer. */
    options.forEach((option) =>
      option.addEventListener('mousedown', (event) => {
        event.preventDefault();
        choose(option);
        close();
        toggle.focus();
      })
    );

    root.addEventListener('keydown', (event) => {
      const { key } = event;

      if (key === 'Escape' && isOpen()) {
        event.preventDefault();
        close();
        toggle.focus();
        return;
      }

      if (key === 'Tab') {
        close();
        return;
      }

      if (!isOpen()) {
        if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(key)) {
          event.preventDefault();
          open();
        }
        return;
      }

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        move(key === 'ArrowDown' ? 1 : -1);
      } else if (key === 'Home' || key === 'End') {
        event.preventDefault();
        setActive(key === 'Home' ? options[0] : options[options.length - 1]);
      } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        const active = options.find((o) => 'active' in o.dataset);
        if (active) choose(active);
        close();
        toggle.focus();
      }
    });

    /* Closes when the focus or the pointer leaves the whole control, which is
       both "clicked elsewhere on the page" and "tabbed away". */
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) close();
    });

    document.addEventListener('pointerdown', (event) => {
      if (isOpen() && !root.contains(event.target)) close();
    });

    /* A value already in the input — a form coming back with what was filled
       in — has to show as the chosen one rather than as the placeholder. */
    if (input.value) {
      choose(options.find((o) => o.dataset.value === input.value) || null);
    }
  });

})();

(() => {
  'use strict';

  /* ── Folded reactions ──
     The customer reactions are clamped at eight lines, and every
     one of them is rendered with a "lees verder" under it. Which of them actually needed
     it is a question only the browser can answer: these are pasted e-mails
     where every line sits in a <p> of its own, so 290 characters over nine
     paragraphs comes out taller than 630 over one, and no length the server
     could measure predicts it.

     So the button is taken away again from the ones that were not cut. That
     way round and not the other, because if this never runs the long ones are
     still openable — the fold is css and the button is a label on a checkbox,
     neither of which needs javascript.

     Runs again on every batch the endless scrolling appends. */
  const trimFolds = (root = document) => {
    root.querySelectorAll('[data-fold]').forEach((quote) => {
      const label = quote.parentElement?.querySelector('label[for]');
      if (!label) return;

      const cut = quote.scrollHeight > quote.clientHeight + 2;

      /* An inline style and not the `hidden` attribute: preflight writes that
         one as `[hidden]:where(...)`, which carries no specificity at all, and
         the `inline-flex` this label wears as a class walks straight over it. */
      label.style.display = cut ? '' : 'none';
    });
  };

  /* ── Masonry ──
     See `.masonry` in app.css for why `columns` was not it. Here: measure each
     card and tell the grid how many one-pixel rows it needs, so the items pack
     into the shortest column instead of being dealt out six to a lane.

     Runs after trimFolds() every time, never before — a fold changes the very
     height this measures, and a span taken before it would be wrong by exactly
     the piece that was cut off.

     Per <ul> and not over the whole page: each batch the endless scrolling
     appends is its own grid, so a new one never reshuffles what someone is in
     the middle of reading. */
  const layoutMasonry = () => {
    document.querySelectorAll('[data-endless]').forEach((houder) => {
      const lijsten = [...houder.querySelectorAll(':scope > ul')];
      const grid = lijsten[0];

      if (!grid) return;

      /* One grid for the whole page and not one per batch. Batching was there
         for `columns`, which rebalances every card above the new ones the
         moment they arrive — the reaction someone was reading would move under
         them. Spans do not do that: placement runs over the items in order, so
         what is already down stays where it is and only the new ones are
         placed. What batching costs instead is a seam, a ragged row of ends
         with the next batch starting level below it, and that is the hole. */
      lijsten.slice(1).forEach((extra) => {
        grid.append(...extra.querySelectorAll(':scope > li'));
        extra.remove();
      });

      grid.classList.add('masonry');

      const gap = parseFloat(getComputedStyle(grid).columnGap) || 20;

      /* No need to clear the last span first: `align-items: start` in the
         stylesheet keeps every card at its own height whatever the grid
         reserved for it, so what comes back here is the content and not the
         previous answer. */
      grid.querySelectorAll(':scope > li').forEach((card) => {
        const hoogte = card.getBoundingClientRect().height;
        card.style.setProperty('--rows', String(Math.ceil(hoogte + gap)));
      });
    });
  };

  /* The reactions page is the one with the masonry and the endless scrolling;
     the home page shows three of the same cards in a plain grid and folds them
     the same way. So the fold hangs on `[data-fold]` anywhere in the page and
     layoutMasonry() is left to find nothing when there is no `[data-endless]`
     to lay out. */
  const foldList = document.querySelector('[data-endless]');

  if (document.querySelector('[data-fold]')) {
    const herteken = (root) => {
      trimFolds(root);
      layoutMasonry();
    };

    herteken();
    foldList?.addEventListener('endless:batch', () => herteken(foldList));

    /* A web font landing after this ran changes every one of these heights. */
    if (document.fonts?.ready) document.fonts.ready.then(() => herteken());

    /* And so does a resize: the columns get narrower, the text rewraps, and
       every span is a pixel count that no longer describes anything. */
    let wacht;
    window.addEventListener('resize', () => {
      clearTimeout(wacht);
      wacht = setTimeout(() => herteken(), 150);
    });

    /* Opening or closing a reaction changes its height by definition. */
    foldList?.addEventListener('change', (event) => {
      if (event.target.matches('input[type="checkbox"]')) layoutMasonry();
    });
  }
})();

/* ═══════════════════════════════════════════════════════════
   The newsletter window.

   The footer has carried a "Nieuwsbrief" button since the old
   theme, and in the rebuild it lost what stood behind it: the
   popup lived in the old _footer.php and its handler in the old
   jQuery app.js, and neither came along. The button did nothing.

   The endpoint never went away — /site/services/?action=
   subscribeToNewsletter — and it answers as it always did:
   `errors`, the names of the fields that are wrong, or
   `successMessage`, a piece of webcopy to show.

   `?subscribe` in the address opens the window straight away,
   as it did before, for a link in a mail or a campaign.
   ═══════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  const overlay = document.querySelector('[data-dialog-newsletter]');
  if (!overlay) return;

  const modal = createModal(overlay);
  const form = overlay.querySelector('[data-newsletter-form]');
  const result = overlay.querySelector('[data-newsletter-result]');
  const button = form.querySelector('button[type="submit"]');

  document.querySelectorAll('[data-popup="newsletter"]').forEach((link) =>
    link.addEventListener('click', (event) => {
      event.preventDefault();
      modal.open();
    })
  );

  overlay
    .querySelectorAll('[data-close-newsletter]')
    .forEach((close) => close.addEventListener('click', () => modal.close()));

  /* The answer replaces the form; an error leaves the form where it is, so
     the visitor can try again without typing everything twice. */
  const show = (html, replaceForm) => {
    result.innerHTML = html;
    result.hidden = false;
    form.hidden = replaceForm;
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    button.disabled = true;
    result.hidden = true;
    form.querySelectorAll('[aria-invalid]').forEach((field) => field.removeAttribute('aria-invalid'));

    try {
      const response = await fetch('/site/services/?action=subscribeToNewsletter', {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
      });
      const data = await response.json();

      if (data.errors?.length) {
        data.errors.forEach((name) => form.elements[name]?.setAttribute('aria-invalid', 'true'));
        form.elements[data.errors[0]]?.focus();

        return;
      }

      /* No errors and nothing to show is what a shop without a newsletter
         list gets back from the endpoint. Saying so beats a window that
         silently stays as it was. */
      if (!data.successMessage) throw new Error('no answer');

      show(data.successMessage, true);
    } catch {
      show(result.dataset.error, false);
    } finally {
      button.disabled = false;
    }
  });

  if (new URLSearchParams(window.location.search).has('subscribe')) modal.open();
})();

/* ═══════════════════════════════════════════════════════════
   Forms: one way of saying what is wrong.

   Until 16-09-2026 every form but the newsletter left this to the browser.
   That meant a balloon in the browser's own wording — different in Chrome,
   Safari and Firefox, in whatever language the browser is set to rather than
   the language of the shop — and it never reached the page at all: `required`
   blocks the submit, so the server's own check, which paints the field red and
   writes a sentence, never ran (Steve, 16-09-2026).

   So the page says it instead: a red field, a sentence under it, a notice at
   the top of the form, and the first bad field in view. `.textfield`,
   `.textfield-error` and `.message-error` already exist; this is what switches them
   on. The wording comes from the shop (`T.form`), so gartenposter.de says it
   in German.

   `noValidate` is set from here and not in the markup: without javascript the
   browser's own check is better than none, and the server validates either
   way. The form asks for this by carrying `data-validate`.
   ═══════════════════════════════════════════════════════════ */
(() => {
  const forms = document.querySelectorAll('form[data-validate]');
  if (!forms.length) return;

  const text = (key, fallback) => window.T?.form?.[key] || fallback;

  /* Which sentence belongs to which kind of fault. A checkbox that has to be
     ticked is its own case: "vul dit veld in" is not what a consent box is
     asking for. */
  const sentenceFor = (field) => {
    const state = field.validity;

    if (state.valueMissing) {
      return field.type === 'checkbox' || field.type === 'radio'
        ? text('consent', 'Zet een vinkje om verder te gaan.')
        : text('required', 'Vul dit veld in.');
    }

    if (state.typeMismatch && field.type === 'email') {
      return text('email', 'Vul een geldig e-mailadres in.');
    }

    if (state.badInput || state.stepMismatch || state.rangeOverflow || state.rangeUnderflow) {
      return text('number', 'Vul een getal in.');
    }

    return text('required', 'Vul dit veld in.');
  };

  /* Where the sentence goes. A tick box lives inside its label and the words
     beside it are part of it, so the sentence goes INSIDE that label —
     between the two it would be a sibling in a `flex flex-col gap-4` form and
     the gap would push it a whole row away from the box it belongs to, with a
     white line through the tint. Everything else gets it directly under the
     field. */
  const anchorFor = (field) => field.closest('.pick') || field;

  const sentenceNode = (field, make) => {
    const anchor = anchorFor(field);
    const inside = anchor.classList.contains('pick');
    let node = inside
      ? anchor.querySelector(':scope > .textfield-error[data-field-error]')
      : anchor.nextElementSibling;

    /* `hasAttribute` and not `dataset.fieldError`: the attribute is written
       without a value, and an empty string is falsy — read that way the
       sentence is never found again, so it is never taken away and a second
       attempt writes a second one underneath it. */
    if (!node || !node.classList.contains('textfield-error') || !node.hasAttribute('data-field-error')) {
      if (!make) return null;

      node = document.createElement('span');
      node.className = 'textfield-error';
      node.dataset.fieldError = '';

      if (inside) {
        anchor.append(node);
      } else {
        anchor.after(node);
      }
    }

    return node;
  };

  const clear = (field) => {
    field.removeAttribute('aria-invalid');
    sentenceNode(field, false)?.remove();
  };

  const mark = (field) => {
    field.setAttribute('aria-invalid', 'true');
    sentenceNode(field, true).textContent = sentenceFor(field);
  };

  /* The notice at the top. Written by the template where there is one — the
     contact page has had a block for the server's own message for a while —
     and made here where there is not. */
  const summaryFor = (form) => {
    let node = form.querySelector('[data-form-summary]');

    if (!node) {
      node = document.createElement('div');
      node.className = 'message message-error mb-6';
      node.dataset.formSummary = '';
      node.setAttribute('role', 'alert');
      node.hidden = true;
      form.prepend(node);
    }

    return node;
  };

  forms.forEach((form) => {
    /* From here on the page does the telling, so the browser stops. */
    form.noValidate = true;

    const summary = summaryFor(form);

    form.addEventListener('submit', (event) => {
      /* `offsetParent` is null for anything folded away — the second address
         on the checkout hides in css while "same as billing" is ticked, and a
         required field nobody can see would stop the form with a complaint
         pointing at nothing. The server checks those as well, and it copies
         the billing address into them when the box is ticked. */
      const visible = (field) => field.offsetParent !== null || field.type === 'hidden';

      const bad = [...form.elements].filter(
        (field) =>
          typeof field.checkValidity === 'function' &&
          !field.disabled &&
          field.type !== 'hidden' &&
          visible(field) &&
          !field.checkValidity()
      );

      [...form.elements].forEach((field) => field.removeAttribute && clear(field));

      if (!bad.length) {
        summary.hidden = true;

        return;
      }

      event.preventDefault();

      bad.forEach(mark);

      summary.textContent = text('summary', 'Er ontbreekt nog iets. Kijk de rood gemarkeerde velden na.');
      summary.hidden = false;

      /* The first one that needs attention, in the middle of the screen: on a
         phone a field near the bottom would otherwise be focused under the
         keyboard. */
      bad[0].focus({ preventScroll: true });
      anchorFor(bad[0]).scrollIntoView({ block: 'center', behavior: 'smooth' });
    });

    /* Typing is the visitor answering the complaint, so it goes away as they
       do — per field, and the notice at the top once nothing is left. */
    form.addEventListener('input', (event) => {
      const field = event.target;
      if (!field.hasAttribute?.('aria-invalid')) return;

      /* Still not right, but differently: an empty box that was "vul dit veld
         in" becomes an address that will not pass the moment there is
         something in it, and the sentence has to keep up. */
      if (!field.checkValidity()) {
        const node = sentenceNode(field, false);
        if (node) node.textContent = sentenceFor(field);

        return;
      }

      clear(field);

      if (!form.querySelector('[aria-invalid]')) summary.hidden = true;
    });

    form.addEventListener('change', (event) => {
      const field = event.target;
      if (field.hasAttribute?.('aria-invalid') && field.checkValidity()) {
        clear(field);

        if (!form.querySelector('[aria-invalid]')) summary.hidden = true;
      }
    });
  });
})();

/* ── "Kies of upload uw foto" ──
   The window the old site opened behind "kies je tuinposter", back for the
   places that ask the question before anything has been chosen. Here and not
   in home.js since a suspension system's page opens it too (4 Oct 2026); a
   page includes _choose_dialog.php or this finds nothing and stops.

   Two kinds of openers. The steps under the home slider and the order button
   on a suspension page are ours and say so with [data-open-choose]. The other
   lives in a slide caption, which is a single webcopy field carrying the whole
   thing — heading, line and buttons — so the marker has to be something an editor can put on a link and tiptap will keep.
   A class is exactly that: admin.js already tells its link extension to hold
   on to `class`, which is how the green button in a caption works, and there
   is a toolbar button beside it now that writes this one.

   The fallback is the first link in the first caption, which is what this ran
   on before the class existed. It keeps the button working on a caption that
   has not been marked yet, and it stops at the first slide on purpose — the
   others carry their own invitations and go where they say they go. */
(() => {
  'use strict';

  const overlay = document.querySelector('[data-dialog-choose]');

  if (!overlay) return;

  const dialog = createModal(overlay);

  const gemarkeerd = [...document.querySelectorAll('.slide-caption a.choose-photo')];

  const openers = [
    ...document.querySelectorAll('[data-open-choose]'),
    ...(gemarkeerd.length ? gemarkeerd : [document.querySelector('.slide-caption a')]),
  ].filter(Boolean);

  openers.forEach((opener) => {
    opener.addEventListener('click', (event) => {
      event.preventDefault();
      dialog.open();
    });
  });

  overlay.querySelectorAll('[data-close-choose]').forEach((button) => {
    button.addEventListener('click', () => dialog.requestClose());
  });
})();

